import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import LogRocket from 'logrocket';

import { Error, LoaderSkeleton, LoaderView } from '@utilities';
import { tabStatus } from '@helpers';

import { cancelReport, readReportTab } from '@api/reports';

import Comments from './components/Comments';
import KeyTakeaways from './components/KeyTakeaways';
import Visualizations from './components/Visualizations';

import { getTabStatus, getTabStatusMessage } from './utilities/helpers';

import styles from './_index.module.scss';

export const LOADING_MESSAGES = [
  "Learn even more about this report and all the features of Insights, <a href='https://help.insights.numerator.com/en/' rel='noreferrer' target='_blank'>here</a>",
  'There is a fine line between a numerator and a denominator… but only a fraction of you will have time to process that before your report finishes running.',
  "Click <a href='https://www.numerator.com/resources?utm_source=insights.numerator.com&utm_medium=referral&utm_campaign=report-processing' rel='noreferrer' target='_blank'>here</a> to see the latest analysis & content from across Numerator",
  'Your report is pulled from a list of over 400,000 receipts transcribed daily!',
];

export const TAB_STATUS_POLLING_INTERVAL = 5000;

const Tab = ({
  answers,
  askWhyInsightsDocument,
  askWhyInstantSurveys,
  askWhyPeopleGroups,
  askWhySurveyType,
  createdDateTime,
  documentId,
  exportData,
  hasComments,
  isReadOnly,
  newComment,
  onAskWhySubmit,
  onComplete,
  peopleGroupTypes,
  publicToken,
  reportId,
  reportType,
  setDownloadData,
  tabId,
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [prevProgress, setPrevProgress] = useState(0);
  const [progress, setProgress] = useState(0);

  const navigate = useNavigate();

  const report = useSelector((state) =>
    state?.websocket?.reports?.find((report) => report?.id === reportId)
  );
  const isWebsocketConnected = useSelector((state) => state?.websocket?.isConnected);
  const tabs = useSelector((state) => state?.websocket?.tabs);

  const status = getTabStatus({ report, tabId });
  const tabReport = tabs?.find((tab) => tab.reportId === reportId && tab.tabId === tabId);
  const newProgress = tabReport?.progress ? tabReport?.progress : 0;

  const handleCancelReport = async () => {
    try {
      await cancelReport({ reportId });

      navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (status === tabStatus.COMPLETED) {
      setIsProcessing(false);
    }
  }, [status]);

  useEffect(() => {
    if (!isProcessing || !tabId || isWebsocketConnected) return;

    const controller = new AbortController();
    const signal = controller.signal;
    let timeoutId;

    const checkTabStatus = async () => {
      try {
        const response = await readReportTab({ publicToken, reportId, signal, tabId });

        if (response?.data?.status >= tabStatus.COMPLETED) {
          setData(response?.data);
          onComplete();
          setIsProcessing(false);
          return;
        }
      } catch (error) {
        if (signal.aborted) return;

        console.error(error);

        if (error?.response) {
          setError(error);
          setIsProcessing(false);
          return;
        }
      }

      if (!signal.aborted) {
        timeoutId = setTimeout(checkTabStatus, TAB_STATUS_POLLING_INTERVAL);
      }
    };

    timeoutId = setTimeout(checkTabStatus, TAB_STATUS_POLLING_INTERVAL);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [isProcessing, isWebsocketConnected, onComplete, publicToken, reportId, tabId]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getData = async () => {
      setData(null);
      setError(null);

      try {
        const response = await readReportTab({ publicToken, reportId, signal, tabId });

        if (response?.data?.status < tabStatus.COMPLETED) {
          setIsProcessing(true);
        } else {
          setData(response?.data);
        }
      } catch (error) {
        console.error(error);

        if (error?.response) {
          setError(error);
        }
      }
    };

    if (tabId && !isProcessing && !data) {
      getData();
      LogRocket.track('Report_View', {
        documentName: reportType,
        tabName: `${reportType} - ${exportData.tabName}`,
      });
    }

    return () => controller.abort();
  }, [data, isProcessing, tabId]);

  useEffect(() => {
    setData(null);
    setIsProcessing(false);
  }, [tabId]);

  useEffect(() => {
    newProgress > 95 ? setProgress(newProgress) : setProgress(prevProgress);
    setPrevProgress(newProgress);
    const intervalId = setInterval(() => {
      setProgress((currentProgress) =>
        currentProgress < newProgress - 10
          ? newProgress - 10
          : currentProgress < newProgress
            ? currentProgress + 1
            : currentProgress
      );
    }, 3000);
    return () => clearInterval(intervalId);
  }, [newProgress]);

  if (isProcessing)
    return (
      <div className={styles['tab-processing']}>
        <LoaderView
          heading={`Status: ${getTabStatusMessage({ status })}...`}
          isNuLoader={documentId === 'count-report'}
          messages={LOADING_MESSAGES}
          progress={progress}
        >
          <button data-testid="cancel-report-button" onClick={handleCancelReport}>
            <span>Cancel Report</span>
          </button>
        </LoaderView>
      </div>
    );

  if (!data && !error)
    return (
      <LoaderSkeleton height={hasComments ? 535 : 255}>
        <rect height="50" rx="4" ry="4" width="250" x="0" y="10" />
        <rect height="50" rx="4" ry="4" width="250" x="275" y="10" />
        <rect height="30" rx="4" ry="4" width="1333" x="0" y="75" />
        <rect height="30" rx="4" ry="4" width="1333" x="0" y="120" />
        <rect height="30" rx="4" ry="4" width="1333" x="0" y="165" />
        <rect height="30" rx="4" ry="4" width="1333" x="0" y="210" />
        <rect height="30" rx="4" ry="4" width="1333" x="0" y="255" />
        {hasComments && <rect height="200" rx="4" ry="4" width="1333" x="0" y="335" />}
      </LoaderSkeleton>
    );

  if (!data?.jsx || error) return <Error status={error?.response?.status || 'failed-report'} />;

  return (
    <div className={styles['tab']}>
      {data?.summary?.text && (
        <KeyTakeaways data={data?.summary} isPublic={publicToken ? true : false} tabId={tabId} />
      )}
      <Visualizations
        answers={answers}
        askWhyInsightsDocument={askWhyInsightsDocument}
        askWhyInstantSurveys={askWhyInstantSurveys}
        askWhyPeopleGroups={askWhyPeopleGroups}
        askWhySurveyType={askWhySurveyType}
        data={data}
        documentId={documentId}
        exportData={exportData}
        isReadOnly={isReadOnly}
        jsx={data?.jsx}
        onAskWhySubmit={onAskWhySubmit}
        peopleGroupTypes={peopleGroupTypes}
        reportId={reportId}
        setDownloadData={setDownloadData}
        tabId={tabId}
      />
      {hasComments && !isReadOnly && <Comments newComment={newComment} tabId={tabId} />}
      <div className={styles['tab-info']}>
        {createdDateTime && <small>Created: {createdDateTime}</small>}
        {reportType && <small>Report Type: {reportType}</small>}
      </div>
    </div>
  );
};

Tab.defaultProps = {
  hasComments: true,
  onComplete: () => {},
};

Tab.propTypes = {
  answers: PropTypes.object,
  askWhyInsightsDocument: PropTypes.object,
  askWhyInstantSurveys: PropTypes.array,
  askWhyPeopleGroups: PropTypes.array,
  askWhySurveyType: PropTypes.object,
  createdDateTime: PropTypes.string,
  documentId: PropTypes.string,
  exportData: PropTypes.exact({
    reportName: PropTypes.string,
    reportType: PropTypes.string,
    tabName: PropTypes.string,
  }),
  hasComments: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  newComment: PropTypes.object,
  onAskWhySubmit: PropTypes.func,
  onComplete: PropTypes.func,
  peopleGroupTypes: PropTypes.array,
  publicToken: PropTypes.string,
  reportId: PropTypes.number,
  reportType: PropTypes.string,
  setDownloadData: PropTypes.func,
  tabId: PropTypes.number,
};

export default memo(Tab);
